import { useInfiniteQuery, type QueryKey } from '@tanstack/react-query'

/**
 * Infinite-scroll hook for the nirixis-admin MONOLITH cursor pagination
 * shape (device-ops endpoints).
 *
 * The monolith's paginated endpoints (pings, command history, geolocations)
 * return one of two very similar shapes:
 *   A. `{ data: T[], pagination: { limit, hasMore, nextCursor, prevCursor } }`
 *      — used by /v1/pings
 *   B. `{ items: T[], nextCursor?, prevCursor?, hasMore }`
 *      — used by /v1/commands/history
 *
 * We accept both via a `resolve` callback the caller supplies. That keeps
 * this hook agnostic to the exact response envelope while still handing back
 * the same `{items, hasNextPage, fetchNextPage, ...}` shape DataTable's
 * infiniteScroll prop expects.
 *
 * Do NOT use this for BFF endpoints — those return `PaginatedResponse<T>`
 * with `nextPageToken` / `totalCount`. Use `useCursorList` for those.
 */
export interface MonolithCursorPage<T> {
  items: T[]
  nextCursor?: string
  hasMore: boolean
}

export interface UseMonolithCursorListOptions<T, R> {
  queryKey: QueryKey
  /**
   * The API call. Receives `{cursor, limit}` plus any extra params the
   * caller pre-binds via closure; must return the raw response.
   */
  queryFn: (args: { cursor?: string; limit: number }) => Promise<R>
  /** Normalize the raw response into `{items, nextCursor, hasMore}`. */
  resolve: (response: R) => MonolithCursorPage<T>
  pageSize?: number
  enabled?: boolean
}

export interface UseMonolithCursorListReturn<T> {
  items: T[]
  isLoading: boolean
  isError: boolean
  error: unknown
  isFetchingNextPage: boolean
  hasNextPage: boolean
  fetchNextPage: () => void
  refetch: () => void
}

const DEFAULT_PAGE_SIZE = 50

export function useMonolithCursorList<T, R>({
  queryKey,
  queryFn,
  resolve,
  pageSize = DEFAULT_PAGE_SIZE,
  enabled = true,
}: UseMonolithCursorListOptions<T, R>): UseMonolithCursorListReturn<T> {
  const { data, isLoading, isError, error, isFetchingNextPage, hasNextPage, fetchNextPage, refetch } =
    useInfiniteQuery({
      queryKey,
      queryFn: ({ pageParam }) =>
        queryFn({ cursor: pageParam as string | undefined, limit: pageSize }).then((raw) => ({
          raw,
          page: resolve(raw),
        })),
      initialPageParam: undefined as string | undefined,
      // Fail-safe: if the response was malformed and `page` never materialized,
      // treat as "no more pages" rather than throw and unmount the tree.
      getNextPageParam: (lastPage) => {
        const page = lastPage?.page
        if (!page || !page.hasMore) return undefined
        return typeof page.nextCursor === 'string' && page.nextCursor.length > 0
          ? page.nextCursor
          : undefined
      },
      enabled,
    })

  // Same guard as useCursorList: some monolith endpoints omit `items` on
  // empty result pages; without `?? []` flatMap would produce phantom rows.
  const items = data?.pages.flatMap((p) => p.page.items ?? []) ?? []

  return {
    items,
    isLoading,
    isError,
    error,
    isFetchingNextPage,
    hasNextPage: hasNextPage ?? false,
    fetchNextPage,
    refetch,
  }
}
